document.addEventListener("DOMContentLoaded", (event) => {
  /*
  * PkAddToCart use in the final step, in <pk-overview-step> component
  */
  class PkAddToCart extends HTMLElement {
    constructor() {
      super();
      this.apiEndpoint = '/products';
      this.store = document.querySelector('pk-store');
      this.customProductName = '';
      this.sensoryValues = {};
      this.userInteracted = false;

      // Translation dictionary for variants (sorry no idea for other solution, so if new product variant, add here manually...)
      this.variantTranslations = {
        '200g / EN GRAINS': { en: '200g / IN GRAINS', fr: '200g / EN GRAINS' },
        '200g / MOULU CAFETIÈRE ITALIENNE': { en: '200g / GROUND FOR MOKA POT', fr: '200g / MOULU CAFETIÈRE ITALIENNE' },
        '200g / MOULU CAFETIÈRE ESPRESSO': { en: '200g / GROUND FOR ESPRESSO', fr: '200g / MOULU CAFETIÈRE ESPRESSO' },
        '200g / MOULU CAFETIÈRE PISTON': { en: '200g / GROUND FOR FRENCH PRESS', fr: '200g / MOULU CAFETIÈRE PISTON' },
        '200g / MOULU CAFETIÈRE FILTRE/CHEMEX': { en: '200g / GROUND FOR FILTER/CHEMEX', fr: '200g / MOULU CAFETIÈRE FILTRE/CHEMEX' }
      };
    }

    connectedCallback() {
      this.variantSelect = this.querySelector('#pk-add-to-cart-variants');
      this.decreaseQuantityButton = this.querySelector('#pk-add-to-cart-decrease-quantity');
      this.increaseQuantityButton = this.querySelector('#pk-add-to-cart-increase-quantity');
      this.quantityInput = this.querySelector('#pk-add-to-cart-quantity-input');
      this.addToCartButton = this.querySelector('#pk-add-to-cart-button');
      this.productHandle = this.closest('.pk-step__wrapper--overview').dataset.productHandle;
      this.priceSpan = document.getElementById('pk-overview-step-price');

      this.successCustomMessage = this.dataset.successMessage;
      this.errorCustomMessage = this.dataset.errorMessage;

      this.setupEventListeners();
      this.fetchProductVariants();

      // Subscribe to store updates to retrieve the custom product name, and use in a private property when add to cart
      if (this.store && this.store.subscribe) {
        this.store.subscribe(this.handleStateUpdate.bind(this));
      } else {
        console.error('PkStore element not found or subscribe method is not available.');
      }
    }

    handleStateUpdate(state) {
      // Retrieve updated values from the store to custom properties
      this.customProductName = state.customProductName || '';
      this.sensoryValues = state.sensoryValues || {};
    }

    setupEventListeners() {
      this.addToCartButton.addEventListener('click', () => this.addToCart());
      this.decreaseQuantityButton.addEventListener('click', () => this.decreaseQuantity());
      this.increaseQuantityButton.addEventListener('click', () => this.increaseQuantity());
      this.quantityInput.addEventListener('input', this.debounce(() => this.handleUserInteraction(), 300));
      this.variantSelect.addEventListener('change', () => this.handleUserInteraction());
    }

    handleUserInteraction() {
      this.userInteracted = true;
      this.updatePrice();
    }

    async fetchProductVariants() {
      try {
        const response = await fetch(`${this.apiEndpoint}/${this.productHandle}.json`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        const product = data.product;
        this.populateVariantOptions(product.variants);
      } catch (error) {
        this.showError('Failed to load product variants.');
      }
    }

    populateVariantOptions(variants) {
      this.variants = variants;
      this.variantSelect.innerHTML = '';
      const isEnglish = this.isEnglishSite();

      variants.forEach(variant => {
        const option = document.createElement('option');
        option.value = variant.id;

        // Dynamically translate variants
        const translatedTitle = this.variantTranslations[variant.title]
          ? this.variantTranslations[variant.title][isEnglish ? 'en' : 'fr']
          : variant.title; // Use original title if no traduction

        option.textContent = translatedTitle;
        option.dataset.price = variant.price;
        this.variantSelect.appendChild(option);
      });
    }

    updatePrice() {
      if (!this.userInteracted) return;

      const selectedVariant = this.variantSelect.options[this.variantSelect.selectedIndex];
      const price = parseFloat(selectedVariant.dataset.price);
      const quantity = Number(this.quantityInput.value);

      if (!isNaN(price) && !isNaN(quantity)) {
        this.priceSpan.textContent = `${(price * quantity).toFixed(2)} €`;
      } else {
        this.priceSpan.textContent = `0,00 €`;
      }
    }

    async addToCart() {
      const variantId = this.variantSelect.value;
      const quantity = parseInt(this.quantityInput.value, 10);

      // For debug private custom properties
      // if (!this.customProductName || !Object.keys(this.sensoryValues).length) {
      //   this.showError('Les informations du produit ne sont pas complètes.');
      //   return;
      // }
      
      this.toggleLoading(true);

      try {
        const formattedSensoryValues = this.formatSensoryValues(this.sensoryValues);
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            id: variantId, 
            quantity: quantity,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname,
            properties: {
              ...this.store.state.propertyValues,
              _produit: this.customProductName,
              _valeursSensorielles: formattedSensoryValues
            }
          }),
        });

        if (response.ok) {
          const data = await response.json();
          this.clearError();
          this.updateSections(data.sections);
          this.showSuccess(this.successCustomMessage);
        } else {
          const errorData = await response.json();
          console.error('Failed to add to cart:', errorData);
          this.showError('Failed to add to cart: ' + errorData.description);
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError(this.errorCustomeMessage);
      } finally {
        this.toggleLoading(false);
      }
    }

    formatSensoryValues(sensoryValues) {
      return Object.entries(sensoryValues)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
    }

    // Updates section html with received sections with Shopify API
    updateSections(sections) {
      this.getSectionsToRender().forEach(({ id, section, selectors }) => {
        selectors.forEach((selector) => {
          const elementToReplace = document.getElementById(id).querySelector(selector) || document.getElementById(id);
          const newInnerHTML = this.getSectionInnerHTML(sections[section], selector);
          
          if (elementToReplace.innerHTML !== newInnerHTML) {
            elementToReplace.innerHTML = newInnerHTML;
          }
        });
      });
    }
  
    // Retrieves the content of a section in the html received contained in a given selector
    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selectors: ['.shopify-section'],
        },
      ];
    }

    decreaseQuantity() {
      if (!this.quantityInput) return;

      let quantity = parseInt(this.quantityInput.value, 10);
      if (quantity > 1) {
        quantity--;
        this.quantityInput.value = quantity;
        this.handleUserInteraction(); 
      }
    }

    increaseQuantity() {
      if (!this.quantityInput) return;

      let quantity = parseInt(this.quantityInput.value, 10);
      quantity++;
      this.quantityInput.value = quantity;
      this.handleUserInteraction();
    }

    updateQuantity() {
      if (!this.quantityInput) return;

      if (this.quantityInput.value < 1) this.quantityInput.value = 1;
      this.handleUserInteraction();
    }

    toggleLoading(isLoading) {
      if (isLoading) {
        this.addToCartButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
      } else {
        this.addToCartButton.classList.remove('loading');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }
    }

    showError(message) {
      const errorMessageElement = this.querySelector('#pk-add-to-cart-error-message');
      if (!errorMessageElement) return;

      errorMessageElement.textContent = message;
      errorMessageElement.style.display = 'block';
    }

    clearError() {
      const errorMessageElement = this.querySelector('#pk-add-to-cart-error-message');
      if (!errorMessageElement) return;

      errorMessageElement.style.display = 'none';
    }

    showSuccess(message) {
      const successMessageElement = this.querySelector('#pk-add-to-cart-success-message');
      if (!successMessageElement) return;

      successMessageElement.textContent = message;
      successMessageElement.style.display = 'block';

      setTimeout(() => {
        successMessageElement.style.display = 'none';
      }, 3000);
    }

    // Debounce a function to limit the rate at which it can fire
    debounce(func, wait) {
      let timeout;
      return function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
      };
    }

    isEnglishSite() {
      return window.location.pathname.includes('/en/');
    }
  }

  customElements.define('pk-add-to-cart', PkAddToCart);
});
